import { isEmpty, isNil } from 'lodash';
import { getTimezone } from '@shared/util/timezone';
import { clearPromptTokenCache, mintPromptToken } from './mintPromptToken';
import {
  AskPromptAction,
  AskPromptAttachment,
  AskPromptBaseline,
  AskPromptRequest,
  AskScope,
} from './types';

interface PostAgUiPromptOptions {
  businessId: number;
  url: string;
  /** Already-serialized `AskPromptRequest`, built by `CoastAskAgent.requestInit`. */
  body: string;
  signal?: AbortSignal;
}

export interface AgUiRequestBodyOptions {
  /**
   * The wire prompt, directives already appended (see `withChatDirectives`).
   *
   * Empty is a real answer rather than a missing one: a Capture of a photo and nothing else has no
   * words to send, and `withCaptureDirectives` returns nothing for it.
   */
  prompt: string;
  scope: AskScope;
  /**
   * What the gateway should do with this turn, for a surface that wants something other than an
   * answer. Omitted by the panel; Capture sends `propose_entity_create`.
   */
  action?: AskPromptAction;
  /**
   * The gateway's id for this conversation. Omitted from the body when empty, which is how a
   * new conversation asks the gateway to start one — see `CoastAskAgent.adoptThreadId`.
   */
  threadId?: string;
  /** The files this turn carries. Omitted from the body when empty. */
  attachments?: AskPromptAttachment[];
  /**
   * The view template a card being built is projected through. Attached to `scope` when the scope
   * can carry it and dropped when it cannot — see `promptScope`.
   */
  viewTemplateId?: string;
  /**
   * What the form the turn is proposing into already holds. Set by a refining Capture and by
   * nothing else — see `captureBaseline`. Omitted from the body when there is none.
   */
  baseline?: AskPromptBaseline;
}

/**
 * The `scope` the gateway is given, with `viewTemplateId` attached only where it is legal.
 *
 * The gateway rejects `viewTemplateId` on an org scope or a scope with no `id`, and rejects it as a
 * 400 on the whole request rather than ignoring it. So the decision is made here rather than trusted
 * to callers: `useCapture` disables its button without a view template, but the scope it holds is
 * resolved asynchronously and reads `{ type: 'org' }` until the channel loads, which is exactly the
 * combination that would be refused.
 *
 * `label` and `channelSlug` are dropped: they are UI-only, and `AskScope` carries them for the
 * panel header (see its doc comment).
 */
function promptScope(
  scope: AskScope,
  viewTemplateId: string | undefined,
): AskPromptRequest['scope'] {
  const canCarryViewTemplate = scope.type !== 'org' && !isEmpty(scope.id);
  return {
    type: scope.type,
    ...(isEmpty(scope.id) ? {} : { id: scope.id }),
    ...(isNil(viewTemplateId) || !canCarryViewTemplate ? {} : { viewTemplateId }),
  };
}

/**
 * Builds the `/prompt` body. `protocol` names the only protocol there is.
 *
 * Empty `attachments`, an empty `threadId`, an empty `prompt` and an absent `baseline` are left off
 * the body rather than sent as `[]` and `''`, so a plain first-turn question is byte-identical to
 * what the gateway received before multi-turn, attachments, actions and baselines existed. Empty
 * rather than nil for the thread id: the agent holds it as a required string (AG-UI's field), so
 * "no thread yet" reaches here as `''` rather than as nothing.
 *
 * An omitted `prompt` is how a photo-only Capture is sent. The gateway owns the instruction wording
 * once `action` is set, so there is nothing left for the client to say — and an empty string would
 * be a turn that said nothing rather than a turn with nothing to say.
 *
 * The time zone is read here rather than passed in, so every request carries it without a
 * call site having to remember to; it is resolved per request because the browser's zone can
 * change under a long-lived session (travel, a DST-less device zone change).
 */
export function buildAgUiRequestBody({
  prompt,
  action,
  scope,
  threadId,
  attachments,
  viewTemplateId,
  baseline,
}: AgUiRequestBodyOptions): AskPromptRequest {
  const timezone = getTimezone();

  return {
    ...(isEmpty(prompt) ? {} : { prompt }),
    ...(isNil(action) ? {} : { action }),
    ...(isEmpty(attachments) ? {} : { attachments }),
    ...(isEmpty(threadId) ? {} : { threadId }),
    scope: promptScope(scope, viewTemplateId),
    ...(isNil(baseline) ? {} : { baseline }),
    // A blank guess is left off rather than sent as `''`, which the gateway would read as a
    // zone rather than as the absence of one.
    ...(isEmpty(timezone) ? {} : { timezone }),
    protocol: 'ag-ui',
  };
}

async function post(accessToken: string, { url, body, signal }: PostAgUiPromptOptions) {
  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
    body,
    signal,
  });
}

/**
 * Mints (or reuses) a prompt token and POSTs an AG-UI prompt request, returning
 * the raw streaming `Response` — AG-UI's own SSE parser consumes the body, so
 * the stream is not read here. On a 401/403 (expired or wrong-scope token) it
 * forces a re-mint and retries once.
 */
export async function postAgUiPrompt(options: PostAgUiPromptOptions): Promise<Response> {
  const { businessId } = options;
  const accessToken = await mintPromptToken(businessId);
  const response = await post(accessToken, options);

  if (response.status !== 401 && response.status !== 403) {
    return response;
  }

  clearPromptTokenCache(businessId);
  const refreshedToken = await mintPromptToken(businessId, { forceRefresh: true });
  return post(refreshedToken, options);
}
